import React, { useEffect, useState } from "react";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import GenderBadge from "./GenderBadge";
import { SendIcon, CancelIcon } from "./icons";

// "Find Matches" — given a client (a User with isActiveClient=true), fetch
// active profiles in the same bureau that satisfy the client's preferences
// and let the bureau create a referral inline. Excludes already-referred
// profiles server-side.
const ClientMatchesModal = ({ isOpen, client, onClose, onReferralCreated }) => {
  const [matches, setMatches] = useState([]);
  const [criteria, setCriteria] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [referringId, setReferringId] = useState(null);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  useEffect(() => {
    if (!isOpen || !client?._id) {
      setMatches([]);
      setCriteria(null);
      setError("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    apiFetch(`/clients/${client._id}/matches`)
      .then((res) => {
        if (cancelled) return;
        setMatches(res?.data || []);
        setCriteria(res?.criteria || null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load matches:", err);
        setError(err.message || "Failed to load matches");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, client?._id]);

  const handleCreateReferral = async (profile) => {
    if (!profile?._id || !client?._id) return;
    try {
      setReferringId(profile._id);
      setGlobalLoadingMessage("Creating referral...");
      setGlobalLoading(true);

      await apiFetch("/referrals/bureau", {
        method: "POST",
        body: JSON.stringify({
          clientId: client._id,
          profileId: profile._id,
        }),
      });

      // Drop this match from the list — it's no longer a candidate.
      setMatches((prev) => prev.filter((m) => m._id !== profile._id));
      if (onReferralCreated) onReferralCreated();
    } catch (err) {
      console.error("Failed to create referral:", err);
      setError(err.message || "Failed to create referral");
    } finally {
      setReferringId(null);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  if (!isOpen || !client) return null;

  const ageRange = criteria?.preferredAgeRange;
  const ageRangeLabel =
    ageRange && (ageRange.min !== null || ageRange.max !== null)
      ? `${ageRange.min ?? "—"}–${ageRange.max ?? "—"}`
      : "Any";
  const arrLabel = (a) =>
    Array.isArray(a) && a.length > 0 ? a.join(", ") : "Any";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden rounded-xl bg-[var(--color-surface)] shadow-xl">
        {/* HEADER */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-[var(--color-border)] flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-[var(--color-text-strong)]">
              Suggested Matches
            </h2>
            <p className="text-sm text-[var(--color-text-muted)]">
              For {client?.user?.fullName || client?.fullName || "client"}
              {client?.user?.referenceId
                ? ` (${client.user.referenceId})`
                : client?.referenceId
                  ? ` (${client.referenceId})`
                  : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {criteria && (
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3 text-sm">
              <div className="font-medium text-[var(--color-text-strong)] mb-1">
                Preferences used
              </div>
              <div className="grid gap-1 text-[var(--color-text)]">
                <div>
                  <span className="font-medium">Gender:</span>{" "}
                  <span className="capitalize">{criteria.preferredGender || "Any"}</span>
                </div>
                <div>
                  <span className="font-medium">Age Range:</span> {ageRangeLabel}
                </div>
                <div>
                  <span className="font-medium">Cities:</span>{" "}
                  {arrLabel(criteria.preferredCities)}
                </div>
                <div>
                  <span className="font-medium">Professions:</span>{" "}
                  {arrLabel(criteria.preferredProfessions)}
                </div>
                <div>
                  <span className="font-medium">Castes:</span>{" "}
                  {arrLabel(criteria.preferredCastes)}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-sm text-[var(--color-text-muted)] text-center py-8">
              Loading matches…
            </div>
          ) : matches.length === 0 ? (
            <div className="text-sm text-[var(--color-text-muted)] text-center py-8">
              No matching profiles found. Try relaxing this client's preferences.
            </div>
          ) : (
            <div className="space-y-2">
              {matches.map((m) => {
                const heightStr =
                  m.heightFeet || m.heightInches
                    ? `${m.heightFeet ?? "—"}ft ${m.heightInches ?? 0}in`
                    : null;
                return (
                  <div
                    key={m._id}
                    className="rounded-lg border border-[var(--color-border)] p-3 flex items-start justify-between gap-3"
                  >
                    <div className="text-sm">
                      <div className="font-semibold text-[var(--color-text-strong)] flex items-center flex-wrap gap-2">
                        <span>{m.fullName || "Unknown"}</span>
                        <GenderBadge gender={m.gender} />
                        <span className="text-xs font-normal text-[var(--color-text-muted)]">
                          ID {m.referenceId || "—"}
                        </span>
                      </div>
                      <div className="text-[var(--color-text-muted)] flex flex-wrap gap-x-2 gap-y-0.5 mt-1">
                        {m.age && <span>{m.age} yrs</span>}
                        {m.gender && <span className="capitalize">• {m.gender}</span>}
                        {heightStr && <span>• {heightStr}</span>}
                        {m.maritalStatus && (
                          <span className="capitalize">• {m.maritalStatus}</span>
                        )}
                      </div>
                      <div className="text-[var(--color-text-muted)] flex flex-wrap gap-x-2 gap-y-0.5">
                        {m.city && <span>{m.city}</span>}
                        {m.profession && <span>• {m.profession}</span>}
                        {(m.cast || m.caste) && <span>• {m.cast || m.caste}</span>}
                        {m.monthlyIncome ? (
                          <span>• Rs{Number(m.monthlyIncome).toLocaleString()}/mo</span>
                        ) : null}
                      </div>
                    </div>
                    <button
                      onClick={() => handleCreateReferral(m)}
                      disabled={referringId === m._id}
                      className="whitespace-nowrap inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-50 px-3 py-2 text-sm font-semibold transition-colors"
                    >
                      <SendIcon className="w-4 h-4" />
                      {referringId === m._id ? "Creating…" : "Create Referral"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-surface)] flex-shrink-0">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] px-4 py-2 text-sm font-semibold transition-colors"
          >
            <CancelIcon className="w-4 h-4" />
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientMatchesModal;
